import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Loading } from '@nextui-org/react';
import { mstore } from '../../store';
import checkAccess from '../../lib/checkAccess';
import { updateTitle } from "../../lib/utils";
import { Lightning } from '../../icons/Lightning';

// Product Card Component
const Product = ({
  history,
  href,
  name,
  isActive,
  disable,
  showLogin
}) => {
  const handleClick = () => {
    if (isActive && !disable) {
      history.push(href);
    }
  };

  return (
    <div className="card w-full bg-base-100 shadow-xl mb-3 hover:shadow-2xl transition-shadow duration-300">
      <div className="card-body">
        <div className="card-actions justify-between items-center">
          <h2 className="card-title text-xl font-bold text-gray-800">{name}</h2>
          {showLogin && (
            <button 
              onClick={handleClick}
              disabled={!isActive || disable}
              className={`btn ${
                isActive && !disable 
                  ? 'bg-blue-500 hover:bg-blue-700' 
                  : 'bg-gray-300 cursor-not-allowed'
              } text-white font-bold transition-colors duration-200`}
            >
              Enter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function for product href
const getHrefForProduct = (product) => {
  switch (product.product_id) {
    case 1:
      return '/driver/dashboard';
    default:
      return 'Unconfigured Product';
  }
};

// Main Products Component
const Products = ({ history, showLogin = false, products: initialProducts = [], initialState, onSelectionChange }) => {
  const [products, setProducts] = useState(initialProducts);

  const clientId = useSelector(state => 
    state.client ? state.client.id : mstore.getPath(["user", "company_id"])
  );

  useEffect(() => {    
    // Set initial state if provided
    if (initialState) {
      setProducts(initialState.products || []);
    }

    // Check access and update title
    checkAccess(clientId);
    updateTitle("Product Management");
  }, [clientId, initialState]);

  const canViewDriver = mstore.get('user', 'isAdmin') ||
    mstore.get('user', 'isSuperAdmin') ||
    mstore.get('user', 'isDriverAdmin') ||
    mstore.get('user', 'isDriverPortalUser');

  return (
    <div className="px-4 py-12 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-4 lg:py-12">
      <div className="grid gap-5 row-gap-10 lg:grid-cols-2">
        {/* Welcome Section */}
        <div className="flex flex-col justify-center">
          <div className="max-w-xl mb-6">
            <h2 className="max-w-lg mb-6 font-sans text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl sm:leading-none">
              Welcome to {' '}
              <span className="relative inline-block text-blue-500">
                My Truckin' Tech™
              </span>
            </h2>
            <p className="text-base text-gray-700 md:text-lg">
              Please head over to{' '}
              <a 
                className="link link-primary hover:text-blue-700 transition-colors duration-200" 
                href="https://www.mytruckin.tech/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                https://www.mytruckin.tech/
              </a>
              {' '}for up to date information regarding our platform.
            </p>
          </div>

          {/* Features Section */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <p className="mb-6 text-sm font-bold tracking-widest uppercase text-blue-500">
              Features
            </p>
            <div className="grid space-y-4 sm:gap-4 sm:grid-cols-2 sm:space-y-0">
              <ul className="space-y-4">
                {[
                  "Built on AWS for high availability",
                  "Fully encrypted and secure",
                  "Optimal performance",
                  "Driver focused, geared towards retention"
                ].map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3 group">
                    <Lightning className="text-blue-500 mt-1 group-hover:scale-110 transition-transform duration-200" />
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{feature}</span>
                  </li>
                ))}
              </ul>
              <ul className="space-y-4">
                {[
                  "Easy to use & deploy",
                  "World class support",
                  "Innovation is driven by customers and drivers",
                  "All features drive operational efficiencies"
                ].map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3 group">
                    <Lightning className="text-blue-500 mt-1 group-hover:scale-110 transition-transform duration-200" />
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className='w-full flex'>
          <div className="divider divider-horizontal"></div>
          <div className='w-5/6 p-3'>
            {products.length > 0 ? (
              products.map(product => (
                <Product
                  key={product.product_id}
                  history={history}
                  disable={product.product_id === 1 && !canViewDriver}
                  href={getHrefForProduct(product)}
                  name={product.product}
                  showLogin={showLogin}
                  isActive={product.is_active}
                />
              ))
            ) : (
              <div className="w-full pt-4 flex justify-center items-center">
                <Loading size='lg'/>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;